import { startOfDay, isBefore, isToday } from "date-fns";
import { getItemsFromCollection } from "directus/src/operations";
import { useQuery } from "@tanstack/react-query";
import { useCurrencies } from "@/modules/reports/contexts/currency-context";
import { formatDateForAPI } from "utils/utils";
import { TODAY } from "@/lib/utils";

const calculateOverdueStats = (
  delayedBillables,
  overdueInvoices,
  convertToINR
) => {
  // Calculate total delayed amount
  const delayedAmount = delayedBillables.reduce((sum, billable) => {
    const amount = billable.amount || 0;
    const convertedAmount = convertToINR(amount, billable.currency);

    return sum + convertedAmount;
  }, 0);

  // Calculate total overdue amount
  const overdueAmount = overdueInvoices.reduce((sum, invoice) => {
    const amount = invoice.adjusted_amount || invoice.amount || 0;
    const convertedAmount = convertToINR(amount, invoice.currency);
    return sum + convertedAmount;
  }, 0);

  return {
    delayedAmount,
    overdueAmount,
    delayedCount: delayedBillables.length,
    overdueCount: overdueInvoices.length,
  };
};

export const useOverdueStats = ({ dateRange }) => {
  const { convertToINR } = useCurrencies();

  return useQuery({
    queryKey: ["overdue-stats", dateRange],
    queryFn: async () => {
      const delayedBillablesQuery = {
        fields: ["id", "amount", "currency", "billing_date"],
        filter: {
          status: { _in: ["contracted", "pending_invoice"] },
          billing_date: {
            _lt: TODAY,
          },
          job: { project: { _nnull: true } },
        },
        limit: -1,
      };

      const overdueInvoicesQuery = {
        fields: [
          "id",
          "amount",
          "adjusted_amount",
          "currency",
          "due_date",
          "status",
        ],
        filter: {
          status: { _eq: "billed" },
          due_date: {
            _lt: TODAY,
          },
          job: { project: { _nnull: true } },
        },
        limit: -1,
      };

      const [delayedBillables, overdueInvoices] = await Promise.all([
        getItemsFromCollection("billables", delayedBillablesQuery),
        getItemsFromCollection("billables", overdueInvoicesQuery),
      ]);

      return calculateOverdueStats(
        delayedBillables,
        overdueInvoices,
        convertToINR
      );
    },
  });
};
